import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CategoryFormComponent } from '../request-settings-dialog/components/category-form/category-form.component';
import { DepartmentFormComponent } from '../request-settings-dialog/components/department-form/department-form.component';
import { StatusFormComponent } from '../request-settings-dialog/components/status-form/status-form.component';
import { RequestCategory } from '../../models/request-category.model';
import { RequestDepartment } from '../../models/request-department.model';
import { RequestStatus } from '../../models/request-status.model';

@Component({
    selector: 'app-request-config',
    templateUrl: './request-config.component.html',
    styleUrls: ['./request-config.component.scss'],
})
export class RequestConfigComponent implements OnInit {
    selectedTab = 'categories';

    isCreatingCategory = false;
    isCreatingDepartment = false;
    isCreatingStatus = false;

    selectedCategory: RequestCategory | null = null;
    selectedDepartment: RequestDepartment | null = null;
    selectedStatusId: string | null = null;

    isEditingCategory = false;
    isEditingDepartment = false;
    isEditingStatus = false;

    isSubmitting = false;

    subTabs = [
        { id: 'categories', translationKey: 'request_v2.settings_dialog.tab_categories', route: 'categories' },
        { id: 'departments', translationKey: 'request_v2.settings_dialog.tab_departments', route: 'departments' },
        { id: 'statuses', translationKey: 'request_v2.settings_dialog.tab_statuses', route: 'statuses' },
        { id: 'other', translationKey: 'request_v2.settings_dialog.tab_other', route: 'other' },
    ];

    @ViewChild(CategoryFormComponent) categoryFormComponent: CategoryFormComponent;
    @ViewChild(DepartmentFormComponent) departmentFormComponent: DepartmentFormComponent;
    @ViewChild(StatusFormComponent) statusFormComponent: StatusFormComponent;

    constructor(private changeDetector: ChangeDetectorRef) {}

    ngOnInit(): void {}

    onSubTabChange(tab: string): void {
        this.resetFormState();
        this.selectedTab = tab;
    }

    private resetFormState(): void {
        this.isCreatingCategory = false;
        this.isCreatingDepartment = false;
        this.isCreatingStatus = false;
        this.isEditingCategory = false;
        this.isEditingDepartment = false;
        this.isEditingStatus = false;
        this.selectedCategory = null;
        this.selectedDepartment = null;
        this.selectedStatusId = null;
        this.isSubmitting = false;
    }

    onCreateCategory(): void {
        this.isCreatingCategory = true;
        this.isEditingCategory = false;
        this.selectedCategory = null;
    }

    onEditCategory(category: RequestCategory): void {
        this.isCreatingCategory = true;
        this.isEditingCategory = true;
        this.selectedCategory = category;
    }

    onCategorySaved(_category: RequestCategory): void {
        this.isSubmitting = false;
        this.isCreatingCategory = false;
        this.isEditingCategory = false;
        this.selectedCategory = null;
    }

    onCancelCategoryCreation(): void {
        this.isSubmitting = false;
        this.isCreatingCategory = false;
    }

    onCategoryBack(): void {
        if (this.categoryFormComponent && this.categoryFormComponent.currentStep > 1) {
            this.categoryFormComponent.onPrevious();
        } else {
            this.onCancelCategoryCreation();
        }
    }

    onCategoryLoadingChange(isLoading: boolean): void {
        this.isSubmitting = isLoading;
    }

    onCreateDepartment(): void {
        this.isCreatingDepartment = true;
        this.isEditingDepartment = false;
        this.selectedDepartment = null;
    }

    onEditDepartment(department: any): void {
        this.selectedDepartment = department;
        this.isCreatingDepartment = true;
        this.isEditingDepartment = true;
        this.changeDetector.detectChanges();
    }

    onDepartmentSaved(_department: any): void {
        this.isSubmitting = false;
        this.isCreatingDepartment = false;
        this.isEditingDepartment = false;
        this.selectedDepartment = null;
    }

    onCancelDepartmentCreation(): void {
        this.isSubmitting = false;
        this.isCreatingDepartment = false;
    }

    onCreateStatus(): void {
        this.isCreatingStatus = true;
        this.isEditingStatus = false;
        this.selectedStatusId = null;
    }

    onEditStatus(statusId: string): void {
        this.selectedStatusId = statusId;
        this.isCreatingStatus = true;
        this.isEditingStatus = true;
        this.changeDetector.detectChanges();
    }

    onStatusSaved(_status: RequestStatus): void {
        this.isSubmitting = false;
        this.isCreatingStatus = false;
        this.isEditingStatus = false;
        this.selectedStatusId = null;
    }

    onStatusBack(): void {
        this.isSubmitting = false;
        this.isCreatingStatus = false;
        this.isEditingStatus = false;
        this.selectedStatusId = null;
    }

    get headerTitle(): string {
        if (this.isCreatingCategory) {
            return this.isEditingCategory ? 'request_v2.settings_dialog.edit_category' : 'request_v2.settings_dialog.new_category';
        }
        if (this.isCreatingDepartment) {
            return 'request_v2.settings_dialog.new_department_or_edit';
        }
        if (this.isCreatingStatus) {
            return this.isEditingStatus ? 'request_v2.settings_dialog.edit_status' : 'request_v2.settings_dialog.new_status';
        }
        const tab = this.subTabs.find((t) => t.id === this.selectedTab);
        return tab ? tab.translationKey : this.selectedTab;
    }

    getCreateButtonTranslationKey(): string {
        if (this.selectedTab === 'categories') {
            return 'request_v2.settings_dialog.create_category';
        }
        if (this.selectedTab === 'departments') {
            return 'request_v2.settings_dialog.create_department';
        }
        if (this.selectedTab === 'statuses') {
            return 'request_v2.settings_dialog.create_status';
        }
        return 'request_v2.common.create';
    }

    get showCreateButton(): boolean {
        return (
            !this.isCreatingCategory &&
            !this.isCreatingDepartment &&
            !this.isCreatingStatus &&
            (this.selectedTab === 'categories' || this.selectedTab === 'departments' || this.selectedTab === 'statuses')
        );
    }

    get showBackButton(): boolean {
        return this.isCreatingCategory || this.isCreatingDepartment || this.isCreatingStatus;
    }

    get displayFooter(): boolean {
        return this.isCreatingDepartment || this.isEditingDepartment || this.isCreatingCategory || this.isEditingCategory || this.isCreatingStatus || this.isEditingStatus;
    }

    get footerButtonText(): string {
        if (this.isCreatingStatus) {
            return this.isEditingStatus ? 'request_v2.common.update' : 'request_v2.common.create';
        }
        if (this.isCreatingDepartment) {
            return this.isEditingDepartment ? 'request_v2.common.update' : 'request_v2.common.create';
        }
        if (this.isCreatingCategory) {
            if (this.categoryFormComponent?.currentStep === 2) {
                return this.isEditingCategory ? 'request_v2.common.update' : 'request_v2.common.create';
            }
            return 'request_v2.common.next';
        }
        return 'request_v2.common.next';
    }

    onHeaderBack(): void {
        if (this.isCreatingCategory) {
            this.onCategoryBack();
        } else if (this.isCreatingDepartment) {
            this.onCancelDepartmentCreation();
        } else if (this.isCreatingStatus) {
            this.onStatusBack();
        }
    }

    onHeaderCreate(): void {
        if (this.selectedTab === 'categories') {
            this.onCreateCategory();
        } else if (this.selectedTab === 'departments') {
            this.onCreateDepartment();
        } else if (this.selectedTab === 'statuses') {
            this.onCreateStatus();
        }
    }

    onNextStep(): void {
        if (this.isCreatingCategory) {
            if (this.categoryFormComponent?.currentStep === 2) {
                this.categoryFormComponent.onSubmit();
            } else {
                this.categoryFormComponent?.onNext();
            }
        } else if (this.isCreatingDepartment) {
            this.isSubmitting = true;
            this.departmentFormComponent?.onSubmit();
        } else if (this.isCreatingStatus) {
            this.isSubmitting = true;
            this.statusFormComponent?.onSubmit();
        }
    }
}
